import { useCallback, useEffect, useState } from 'react'
import {
  ApiError,
  formatDateTime,
  getHealth,
  getNotificationStats,
  getStaff,
  inviteStaff,
  removeStaff,
  setStaffRole,
  type HealthCheck,
  type NotificationStats,
  type StaffRow,
} from '../lib/api'
import { Card, Empty, ErrorBox, Kpi, Spinner, WarnBox } from '../components/bits'
import { ConfirmModal, Modal } from '../components/Modal'

/**
 * Portal settings — deployment health, Secretariat accounts, delivery queue.
 *
 * The self-lockout guards are the interesting part, and they live in the
 * database rather than here: an admin cannot demote or remove themselves, and
 * the last remaining admin cannot be demoted at all. Without that, one click
 * locks the college out of its own portal and recovery needs direct database
 * access. The UI disables those controls too, but only so the reason is visible
 * before the click — the server is what actually refuses.
 *
 * Inviting an account goes through the `invite-secretariat` Edge Function rather
 * than happening here, because creating an auth user needs the service-role key
 * and that must never be in this bundle. The function re-checks the caller is an
 * admin using the caller's own token, so the button is a convenience, not the
 * security boundary.
 */
export default function PortalSettings() {
  const [staff, setStaff] = useState<StaffRow[]>([])
  const [queue, setQueue] = useState<NotificationStats | null>(null)
  const [health, setHealth] = useState<HealthCheck | null>(null)
  const [inviting, setInviting] = useState(false)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<string | null>(null)
  const [removing, setRemoving] = useState<StaffRow | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [rows, stats, healthCheck] = await Promise.all([
        getStaff(),
        getNotificationStats().catch(() => null),
        getHealth().catch(() => null),
      ])
      setStaff(rows ?? [])
      setQueue(stats)
      setHealth(healthCheck)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  const adminCount = staff.filter((s) => s.role === 'secretariat_admin').length
  const isAdmin = staff.find((s) => s.is_self)?.role === 'secretariat_admin'

  async function changeRole(row: StaffRow): Promise<void> {
    const next =
      row.role === 'secretariat_admin' ? 'secretariat_staff' : 'secretariat_admin'
    setBusyId(row.id)
    setError(null)
    try {
      await setStaffRole(row.id, next)
      setNotice(
        `${row.name} is now ${next === 'secretariat_admin' ? 'an admin' : 'staff'}.`,
      )
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  async function doRemove(row: StaffRow): Promise<void> {
    setBusyId(row.id)
    setError(null)
    try {
      await removeStaff(row.id)
      setNotice(
        `${row.name} no longer has Secretariat access. Their past decisions ` +
          `remain attributed to them in the audit log.`,
      )
      setRemoving(null)
      await load()
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="stack">
      <div>
        <div className="eyebrow">Administration</div>
        <h1 style={{ marginTop: 8 }}>Portal settings</h1>
        <p className="small" style={{ marginTop: 6 }}>
          Who can use this portal, and whether notifications are actually going out.
        </p>
      </div>

      {error && <ErrorBox>{error}</ErrorBox>}

      {notice && (
        <Card style={{ borderColor: '#2f8fd4' }}>
          <div className="row">
            <span className="small">{notice}</span>
            <div className="spacer" />
            <button className="btn btn-secondary btn-sm" onClick={() => setNotice(null)}>
              Dismiss
            </button>
          </div>
        </Card>
      )}

      {/* ---- Deployment health -------------------------------------- */}
      {health && <DeploymentHealth health={health} />}

      {/* ---- Delivery health ---------------------------------------- */}
      <div>
        <h2 style={{ marginBottom: 'var(--s-4)' }}>Notification queue</h2>
        {queue === null ? (
          <Card>
            <p className="small" style={{ margin: 0 }}>
              Queue statistics are unavailable.
            </p>
          </Card>
        ) : (
          <>
            <div className="kpi-grid">
              <Kpi
                label="Queued"
                value={queue.queued}
                note={
                  queue.oldestQueuedAt
                    ? `Oldest since ${formatDateTime(queue.oldestQueuedAt)}`
                    : 'Nothing waiting'
                }
              />
              <Kpi label="Sent" value={queue.sent} />
              <Kpi
                label="Failed"
                value={queue.failed}
                noteColor={queue.failed > 0 ? 'var(--rejected)' : undefined}
                note={queue.failed > 0 ? 'Check the worker logs' : 'None'}
              />
              <Kpi
                label="Stuck"
                value={queue.stuck}
                note={queue.stuck > 0 ? 'Gave up after 5 attempts' : 'None'}
                noteColor={queue.stuck > 0 ? 'var(--flagged)' : undefined}
              />
            </div>

            {queue.stuck > 0 && (
              <div style={{ marginTop: 'var(--s-4)' }}>
                <WarnBox>
                  {queue.stuck} {queue.stuck === 1 ? 'notification has' : 'notifications have'}{' '}
                  failed five times and will not be retried. That usually means a
                  dead email address or a stale device token — the record is kept
                  so it can be investigated rather than silently dropped.
                </WarnBox>
              </div>
            )}
          </>
        )}
      </div>

      {/* ---- Staff --------------------------------------------------- */}
      <div>
        <div className="row" style={{ marginBottom: 'var(--s-4)' }}>
          <h2>Secretariat accounts</h2>
          <div className="spacer" />
          <button
            className="btn btn-primary"
            onClick={() => setInviting(true)}
            disabled={!isAdmin}
            title={isAdmin ? undefined : 'Only a Secretariat admin may invite accounts'}
          >
            Invite someone
          </button>
        </div>
        <Card style={{ padding: 'var(--s-5)' }}>
          {loading ? (
            <Spinner />
          ) : staff.length === 0 ? (
            <Empty
              title="No accounts found"
              body="This should not happen — you are signed in, so at least your own account should be listed."
            />
          ) : (
            <table>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>MFA</th>
                  <th>Last signed in</th>
                  <th style={{ textAlign: 'right' }} />
                </tr>
              </thead>
              <tbody>
                {staff.map((s) => {
                  const isLastAdmin =
                    s.role === 'secretariat_admin' && adminCount <= 1
                  const locked = s.is_self || isLastAdmin

                  return (
                    <tr key={s.id}>
                      <td>
                        <strong style={{ color: 'var(--text)' }}>{s.name}</strong>
                        {s.is_self && <span className="pill" style={{ marginLeft: 8 }}>You</span>}
                        <br />
                        <span className="small muted">
                          {s.designation ? `${s.designation} · ` : ''}
                          {s.email}
                        </span>
                      </td>
                      <td>
                        <span className="pill">
                          {s.role === 'secretariat_admin' ? 'Admin' : 'Staff'}
                        </span>
                      </td>
                      <td className="small">
                        {s.mfa_enabled ? (
                          <span style={{ color: 'var(--approved)' }}>On</span>
                        ) : (
                          <span style={{ color: 'var(--flagged)' }}>Off</span>
                        )}
                      </td>
                      <td className="small muted">
                        {s.last_login_at ? formatDateTime(s.last_login_at) : 'Never'}
                      </td>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <button
                          className="btn btn-secondary btn-sm"
                          onClick={() => void changeRole(s)}
                          disabled={!isAdmin || locked || busyId === s.id}
                          title={
                            s.is_self
                              ? 'You cannot change your own role'
                              : isLastAdmin
                                ? 'The portal must keep at least one admin'
                                : undefined
                          }
                        >
                          Make {s.role === 'secretariat_admin' ? 'staff' : 'admin'}
                        </button>{' '}
                        <button
                          className="btn btn-danger btn-sm"
                          onClick={() => setRemoving(s)}
                          disabled={!isAdmin || locked || busyId === s.id}
                          title={
                            s.is_self
                              ? 'You cannot remove your own account'
                              : isLastAdmin
                                ? 'The portal must keep at least one admin'
                                : undefined
                          }
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          )}
        </Card>

        {!isAdmin && !loading && (
          <div style={{ marginTop: 'var(--s-4)' }}>
            <WarnBox>
              You are signed in as staff, so these controls are read-only. Only a
              Secretariat admin can change roles or remove accounts.
            </WarnBox>
          </div>
        )}

        <Card style={{ marginTop: 'var(--s-4)' }}>
          <h3>About accounts</h3>
          <p className="small" style={{ marginTop: 8 }}>
            An invitation emails a link; the person sets their own password from
            it, so no password is ever typed on their behalf.
          </p>
          <p className="small" style={{ marginTop: 8 }}>
            A trigger prevents one account from being both a Petarian and
            Secretariat staff, so use a work address that is not already on the
            register.
          </p>
          <p className="small" style={{ marginTop: 8 }}>
            <strong>MFA</strong> is required for Secretariat accounts
            (TECHNICAL-REQUIREMENTS.md §5) and is enforced in Supabase Auth, not
            here. The column above reflects what Supabase reports.
          </p>
        </Card>
      </div>

      {inviting && (
        <InviteModal
          onCancel={() => setInviting(false)}
          onInvited={(message) => {
            setInviting(false)
            setNotice(message)
            void load()
          }}
        />
      )}

      {removing && (
        <ConfirmModal
          title={`Remove ${removing.name}?`}
          body={
            `They will lose access to this portal immediately. Their past ` +
            `decisions stay in the audit log and stay attributed to them — the ` +
            `record of who approved what is not rewritten.`
          }
          confirmLabel="Remove access"
          danger
          busy={busyId === removing.id}
          onCancel={() => setRemoving(null)}
          onConfirm={() => void doRemove(removing)}
        />
      )}
    </div>
  )
}


/**
 * Is this deployment actually wired up?
 *
 * Each row is something that has silently broken a deployment somewhere: an
 * export that fails because the bucket was never created, a queue that stops
 * because the cron job was never scheduled, a register with no admin left on it.
 * Cheaper to surface here than to discover when someone needs an export.
 */
function DeploymentHealth({ health }: { health: HealthCheck }) {
  const cron = health.cronJobs
  const cronOk = Array.isArray(cron) && cron.length >= 2

  const checks: { label: string; ok: boolean | null; detail: string }[] = [
    {
      label: 'Exports bucket',
      ok: health.exportsBucket === 'unknown' ? null : health.exportsBucket === 'ok',
      detail:
        health.exportsBucket === 'ok'
          ? 'Present and private'
          : health.exportsBucket === 'missing'
            ? 'Missing — CSV export will fail'
            : 'Cannot tell from here',
    },
    {
      label: 'Scheduled jobs',
      ok: cron === 'unknown' ? null : cronOk,
      detail:
        cron === 'unknown'
          ? 'pg_cron not available'
          : cron.length === 0
            ? 'None scheduled — notifications will not send'
            : cron.join(', '),
    },
    {
      label: 'Admins',
      ok: health.admins >= 2,
      detail:
        health.admins === 0
          ? 'None — nobody can manage the portal'
          : health.admins === 1
            ? 'Only one. A second avoids a single point of lockout.'
            : `${health.admins} admins`,
    },
    {
      label: 'Erasure requests due',
      ok: health.erasureDue === 0,
      detail:
        health.erasureDue === 0
          ? 'None outstanding'
          : `${health.erasureDue} past the 30-day deadline — is the purge job running?`,
    },
  ]

  const problems = checks.filter((c) => c.ok === false).length

  return (
    <div>
      <h2 style={{ marginBottom: 'var(--s-4)' }}>Deployment health</h2>
      <Card>
        {problems === 0 ? (
          <p className="small" style={{ margin: '0 0 var(--s-4)' }}>
            Everything that can be checked from here looks right.
          </p>
        ) : (
          <div style={{ marginBottom: 'var(--s-4)' }}>
            <WarnBox>
              {problems} {problems === 1 ? 'item needs' : 'items need'} attention.
            </WarnBox>
          </div>
        )}

        <table>
          <tbody>
            {checks.map((c) => (
              <tr key={c.label}>
                <td style={{ width: 180 }}>{c.label}</td>
                <td>
                  <span className="pill">
                    <span
                      className={`dot dot-${
                        c.ok === null ? 'pending' : c.ok ? 'approved' : 'flagged'
                      }`}
                    />
                    {c.ok === null ? 'Unknown' : c.ok ? 'OK' : 'Check'}
                  </span>
                </td>
                <td className="small muted">{c.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  )
}

function InviteModal({
  onCancel,
  onInvited,
}: {
  onCancel: () => void
  onInvited: (message: string) => void
}) {
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [designation, setDesignation] = useState('')
  const [role, setRole] = useState<StaffRow['role']>('secretariat_staff')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function send(): Promise<void> {
    if (name.trim().length < 3) {
      setError('A name is required.')
      return
    }
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim())) {
      setError('Enter a valid work email address.')
      return
    }

    setBusy(true)
    setError(null)
    try {
      const result = await inviteStaff({
        email: email.trim(),
        name: name.trim(),
        designation: designation.trim() || undefined,
        role,
      })
      onInvited(`Invitation sent to ${result.email}. ${result.message}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Modal
      title="Invite a Secretariat account"
      onClose={onCancel}
      footer={
        <>
          <div className="spacer" />
          <button className="btn btn-secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-accent" onClick={() => void send()} disabled={busy}>
            {busy ? 'Sending…' : 'Send invitation'}
          </button>
        </>
      }
    >
      <p className="small" style={{ marginTop: 0 }}>
        They receive an email and set their own password from the link. Use a work
        address — one account cannot be both a Petarian and Secretariat staff.
      </p>

      <div className="stack" style={{ marginTop: 'var(--s-5)', gap: 'var(--s-4)' }}>
        <label style={{ display: 'block' }}>
          <span className="kpi-label">Full name</span>
          <input
            className="input"
            style={{ marginTop: 6 }}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="kpi-label">Work email</span>
          <input
            className="input"
            style={{ marginTop: 6 }}
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="kpi-label">Designation</span>
          <input
            className="input"
            style={{ marginTop: 6 }}
            value={designation}
            onChange={(e) => setDesignation(e.target.value)}
            placeholder="e.g. Verification Officer"
          />
        </label>
        <label style={{ display: 'block' }}>
          <span className="kpi-label">Role</span>
          <select
            className="input"
            style={{ marginTop: 6 }}
            value={role}
            onChange={(e) => setRole(e.target.value as StaffRow['role'])}
          >
            <option value="secretariat_staff">Staff — verify and maintain records</option>
            <option value="secretariat_admin">
              Admin — also manages accounts, exports and the memorial roll
            </option>
          </select>
        </label>
      </div>

      {error && (
        <div className="error-box" role="alert" style={{ marginTop: 'var(--s-4)' }}>
          {error}
        </div>
      )}
    </Modal>
  )
}
